import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Navigation from "../components/Navigation";
import axios from "axios";
import { Bookmark, Heart, Star, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Saved() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSavedPosts();
  }, []);

  const fetchSavedPosts = async () => {
    try {
      const response = await axios.get(`${API}/bookmarks`);
      setPosts(response.data);
    } catch (error) {
      console.error("Failed to fetch saved posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = async (postId) => {
    try {
      await axios.post(`${API}/bookmarks`, { post_id: postId });
      setPosts(posts.filter((p) => p.id !== postId));
      toast.success("Removed from saved");
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-40 bg-white rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 pb-24">
      <Navigation />

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bookmark className="w-6 h-6 text-amber-600" />
            Saved Posts
          </h1>
          <p className="text-gray-500 mt-1">
            {posts.length} saved post{posts.length !== 1 && "s"}
          </p>
        </div>

        {/* Saved Posts */}
        {posts.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Bookmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              No saved posts yet
            </h3>
            <p className="text-gray-500">
              Tap the bookmark icon on any post to save it for later
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <div
                key={post.id}
                className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* User Info */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden">
                      {post.user_photo ? (
                        <img
                          src={post.user_photo}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-amber-600 font-bold">
                          {post.user_name?.[0]}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{post.user_name}</div>
                      <div className="text-xs text-gray-500">
                        Saved {new Date(post.bookmarked_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeBookmark(post.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <p className="text-gray-700 mb-3">{post.content}</p>

                {/* Media */}
                {post.media_url && post.media_type === "image" && (
                  <img
                    src={post.media_url}
                    alt=""
                    className="w-full rounded-lg mb-3"
                  />
                )}

                {/* Reactions */}
                <div className="flex items-center gap-6 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Heart className="w-4 h-4 text-red-500" />
                    {post.hearts || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500" />
                    {post.stars || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    🤗 {post.hugs || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-4 h-4" />
                    Comments
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
